import {
  Component,
  Event,
  EventEmitter,
  Fragment,
  h,
  Listen,
  Prop,
  State,
} from "@stencil/core";
import store, { addToCart } from "@stores/cart.store";
import { AddToCartRequest } from "@apis/requests/add-to-cart-request";
import { AddToCartEvent } from "@apis/model/add-to-cart-event";
import { Cart } from "@apis/model/mini-cart";
import { ContextService } from "@globals/helpers";
import {
  BcpAddToCartNewProps,
  BcpAddToCartNewState,
} from "./bcp-add-to-cart-new-model";
import { PreventMergeEntry } from "../bcp-duplication-entry-modal/bcp-duplication-entry-modal-model";
import { ProductAddedEventDetails } from "../../bcp-product-action-container/bcp-product-action-container-model";
import { CartUpdatedEventDetails } from "../../../../frontend-kit/organisms/f-header/f-cart-flyout/f-cart-flyout-model";
import config from "../../../../environment/environment-config";

@Component({
  tag: "bcp-add-to-cart-new",
})
export class BcpAddToCartNew {
  @Prop() cartLabel: BcpAddToCartNewProps["cartLabel"] = "Add to cart";

  @Prop() modalHeaderLabel: BcpAddToCartNewProps["modalHeaderLabel"];

  @Prop() modalBodyLabel: BcpAddToCartNewProps["modalBodyLabel"];

  @Prop() modalIncreaseLabel: BcpAddToCartNewProps["modalIncreaseLabel"];

  @Prop() modalNewLineLabel: BcpAddToCartNewProps["modalNewLineLabel"];

  @Prop() modalCancelLabel: BcpAddToCartNewProps["modalCancelLabel"];

  @Prop() createCartLabel: BcpAddToCartNewProps["createCartLabel"];

  @Prop({ mutable: true, reflect: true })
  buttonDisabled: BcpAddToCartNewProps["buttonDisabled"] = false;

  @Prop({ mutable: true, reflect: true })
  productCode: BcpAddToCartNewProps["productCode"];

  @Prop({ mutable: true, reflect: true })
  quantity: BcpAddToCartNewProps["quantity"];

  @Prop() visualType: BcpAddToCartNewProps["visualType"] = "primary";

  @Prop() multiCartEnabled: BcpAddToCartNewProps["multiCartEnabled"] = false;

  @State()
  isDuplicateModalVisible: BcpAddToCartNewState["isDuplicateModalVisible"] = false;

  @Event() cartUpdated: EventEmitter<CartUpdatedEventDetails>;

  @Event() productAdded: EventEmitter<ProductAddedEventDetails>;

  @Listen("addedToSelectedCart", { target: "window" })
  async handleCartSelect({
    detail,
  }: CustomEvent<AddToCartEvent>): Promise<void> {
    const { cart, productCode } = detail;
    if (productCode === this.productCode) {
      this.handleAddEntryToCart(cart);
    }
  }

  private cartSelector: HTMLBcpMulticartSelectorElement = null;

  private duplicationModal: HTMLBcpDuplicationEntryModalElement = null;

  private showDuplicationModal = (cart: Cart): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (config?.cartConfig?.skipEntryMergeOverlayConfirmation) reject();
      const preventEntriesMerge = sessionStorage.getItem("preventEntriesMerge");
      if (preventEntriesMerge != null) reject();
      const cartEntry = cart.entries.find(
        (entry) => entry.product.codes.code === this.productCode
      );
      if (cartEntry != null) {
        resolve();
      }
      reject();
    });
  };

  private handleAddToCartButtonClick = () => {
    if (ContextService.isAnonymous() || !this.multiCartEnabled) {
      this.handleAddEntryToCart(store.carts[0]);
      return;
    }
    this.cartSelector.toggleCartSelector();
  };

  private handleAddEntryToCart(cart: Cart) {
    this.showDuplicationModal(cart)
      .then(() => this.duplicationModal.openModel(cart))
      .catch(() => {
        this.requestToApi(cart);
      });
  }

  private requestToApi = (cart: Cart) => {
    const preventEntriesMerge =
      sessionStorage.getItem("preventEntriesMerge") ==
      PreventMergeEntry.PREVENT;
    addToCart(
      new AddToCartRequest(
        cart,
        [{ code: this.productCode, quantity: this.quantity }],
        preventEntriesMerge
      )
    ).then(() => {
      this.cartUpdated.emit({ cartCode: cart.code });
      this.productAdded.emit({ productCode: this.productCode });
    });
  };

  render() {
    return (
      <Fragment>
        <f-button
          variant={this.visualType}
          fullWidth={true}
          disabled={this.buttonDisabled}
          {...(!this.buttonDisabled && {
            onClick: this.handleAddToCartButtonClick,
          })}
        >
          <f-icon iconName="shoppingcart-add" titleText={this.cartLabel} />
          {this.cartLabel}
        </f-button>
        {this.multiCartEnabled && (
          <bcp-multicart-selector
            productCode={this.productCode}
            createCartLabel={this.createCartLabel}
            ref={(el) => (this.cartSelector = el)}
          />
        )}
        <bcp-duplication-entry-modal
          ref={(el) => (this.duplicationModal = el)}
          productCode={this.productCode}
          headerLabel={this.modalHeaderLabel}
          bodyLabel={this.modalBodyLabel}
          increaseLabel={this.modalIncreaseLabel}
          newLineLabel={this.modalNewLineLabel}
          cancelLabel={this.modalCancelLabel}
        />
      </Fragment>
    );
  }
}
